﻿import { Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import heatmapOverlay from '../../../assets/images/Mask_Lmdpi.png';
import heatmapBackground from '../../../assets/images/sensorInsoles.png';
import { AlertList } from '../../../components/documented/AlertList/AlertList';
import Heatmap from '../../../components/Heatmap/Heatmap';
import { HeatmapSizeEnum } from '../../../components/Heatmap/HeatmapSizeEnum';
import { defaultTransforms, pressureTransforms, temperatureTransforms } from '../../../components/Heatmap/heatmapTransforms';
import { useClientTheme } from '../../../helpers/hooks/useClientTheme';
import {
  getEnumKeyByEnumValue,
  isNullOrUndefined,
} from '../../../helpers/utilityFunctions/utilityFunctions';
import { Alert } from '../../../model/passive/alertMoment';
import { HeatmapData } from '../../../model/passive/heatmapData';
import { PassiveParameterType } from '../../../model/passive/passiveParameterType';
import {
  clearAllAlertDetails,
  getAllAlertDetail,
} from '../../../redux/actions/passiveParameterActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectedDailyMetrics } from '../../../redux/selectors/dailyMetricsSelectors';
import {
  dailyMetricIsLoading,
  selectedAlertDetails,
} from '../../../redux/selectors/passiveParametersSelectors';
import PressureInterpretationTableComponent from './PressureInterpretationTableComponent';
import TemperatureInterpretationTableComponent from './TemperatureInterpretationTableComponent';


const selectTransforms = (type: PassiveParameterType) => {
  switch (type) {
    case PassiveParameterType.AlarmPressure:
      return pressureTransforms;
    case PassiveParameterType.AlarmTemperature:
      return temperatureTransforms;
    default:
      return defaultTransforms;
  }
};

interface Props {
  patientId?: string;
  alertType: PassiveParameterType;
}

export const AlertDetailsComponent: React.FC<Props> = ({
  patientId,
  alertType,
}) => {
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation('alertDetail');
  const { theme } = useClientTheme();

  const [selectedMomentId, setSelectedMomentId] = React.useState(0);

  const selectedData = useSelector(selectedDailyMetrics);
  const selectedAlertData = useSelector(selectedAlertDetails);
  const loading = useSelector(dailyMetricIsLoading);

  const createHeatmapData = () => {
    const momentList = selectedAlertData.map((item) => {
      return item.data.find((alert) => alert.id === selectedMomentId);
    });

    //the selected moment may appear in several lists - so just use the first to render heatmap data
    if (!isNullOrUndefined<Array<Alert | undefined>>(momentList)) {
      return new HeatmapData(
        momentList[0]?.sensorDataLeft,
        momentList[0]?.sensorDataRight
      );
    } else {
      return new HeatmapData('[]', '[]');
    }
  };

  useEffect(() => {
    const processedType =
      getEnumKeyByEnumValue(PassiveParameterType, alertType) || '';

    switch (alertType) {
      case PassiveParameterType.AlarmPressure:
        dispatch(getAllAlertDetail(patientId || '', processedType));

        if (!isNullOrUndefined(selectedData?.latestAlertsPressure)) {
          setSelectedMomentId(selectedData?.latestAlertsPressure.id!);
        }
        break;
      case PassiveParameterType.AlarmTemperature:
        dispatch(
          getAllAlertDetail(
            patientId || '',
            processedType,
            theme.metrics.temperature.unit
          )
        );
        if (!isNullOrUndefined(selectedData?.latestAlertsTemperature)) {
          setSelectedMomentId(selectedData?.latestAlertsTemperature.id!);
        }
        break;
      default:
        break;
    }
    return () => {
      dispatch(clearAllAlertDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number
  ) => {
    setSelectedMomentId(id);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 8 }}>
          <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body1">
              {alertType === PassiveParameterType.AlarmPressure
                ? t('intro.pressure')
                : t('intro.temperature')}
            </Typography>
            <Heatmap
              height={260}
              width={260}
              magnitude={HeatmapSizeEnum.Large}
              data={createHeatmapData()}
              type={alertType!}
              unit={theme.metrics.temperature.unit}
              background={heatmapBackground}
              overlay={heatmapOverlay}
              transforms={selectTransforms(alertType!)}
            />
            {alertType === PassiveParameterType.AlarmPressure ? (
              <PressureInterpretationTableComponent />
            ) : (
              <TemperatureInterpretationTableComponent />
            )}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <AlertList
              alertType={alertType}
              selectedAlertData={selectedAlertData}
              selectedMomentId={selectedMomentId}
              isLoading={loading}
              onClick={handleListItemClick}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AlertDetailsComponent;
